<template>
  <v-card class="kpi-card elevation-0" outlined>
    <v-card-text class="pa-3 pa-sm-5">
      <div v-if="icon" class="kpi-icon d-none d-sm-flex" :class="gradient">
        <v-icon color="white">{{ icon }}</v-icon>
      </div>
      <div class="kpi-content">
        <div class="kpi-label">{{ label }}</div>
        <div class="kpi-value">{{ formattedValue }}</div>
        <div v-if="hasPrevious" class="kpi-delta d-flex align-center" :class="deltaClass">
          <v-icon x-small :color="deltaColor">{{ deltaIcon }}</v-icon>
          <span class="ml-1">{{ deltaText }}</span>
        </div>
        <div v-else-if="subtitle" class="kpi-subtitle d-none d-sm-flex">{{ subtitle }}</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { formatCurrency } from "@/services/webserver.js";

export default {
  name: "KpiDeltaCard",
  props: {
    label: { type: String, required: true },
    value: { type: [Number, String], default: 0 },
    previousValue: { type: [Number, String], default: null },
    format: { type: String, default: "currency" }, // currency | percent | number | decimal
    icon: { type: String, default: null },
    gradient: { type: String, default: "success-gradient" },
    subtitle: { type: String, default: null },
    invertDelta: { type: Boolean, default: false }, // true = lower is better (e.g., discount rate)
  },
  computed: {
    numValue() { return parseFloat(this.value) || 0; },
    numPrevious() { return parseFloat(this.previousValue) || 0; },
    hasPrevious() { return this.previousValue !== null && this.previousValue !== undefined; },
    formattedValue() {
      const v = this.numValue;
      switch (this.format) {
        case "currency": return formatCurrency(v);
        case "percent": return v + "%";
        case "decimal": return v.toFixed(1);
        case "number":
        default: return v.toLocaleString();
      }
    },
    delta() {
      if (!this.hasPrevious || this.numPrevious === 0) return null;
      return ((this.numValue - this.numPrevious) / Math.abs(this.numPrevious)) * 100;
    },
    deltaPoints() {
      if (!this.hasPrevious) return null;
      return this.numValue - this.numPrevious;
    },
    isPositive() {
      if (this.delta === null) return true;
      return this.invertDelta ? this.delta <= 0 : this.delta >= 0;
    },
    deltaColor() { return this.isPositive ? "success" : "error"; },
    deltaIcon() {
      if (this.delta === null || this.delta === 0) return "mdi-minus";
      return this.delta > 0 ? "mdi-arrow-up" : "mdi-arrow-down";
    },
    deltaClass() { return this.isPositive ? "success--text" : "error--text"; },
    deltaText() {
      if (this.delta === null) return "Sin datos previos";
      if (this.format === "percent") {
        const pts = this.deltaPoints;
        const sign = pts >= 0 ? "+" : "";
        return `${sign}${pts.toFixed(1)}pp`;
      }
      const sign = this.delta >= 0 ? "+" : "";
      return `${sign}${this.delta.toFixed(1)}%`;
    },
  },
  methods: { formatCurrency },
};
</script>

<style scoped>
.kpi-card { border-radius: 8px; transition: box-shadow 0.2s ease; }
.kpi-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important; }
.kpi-card .v-card__text { display: flex; align-items: center; gap: 16px; }
.kpi-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.success-gradient { background: linear-gradient(135deg, #1e8e3e, #34a853); }
.primary-gradient { background: linear-gradient(135deg, #1a73e8, #4285f4); }
.warning-gradient { background: linear-gradient(135deg, #f9ab00, #fbbc04); }
.info-gradient { background: linear-gradient(135deg, #1967d2, #4285f4); }
.kpi-label { font-size: 0.75rem; font-weight: 500; color: #5f6368; text-transform: uppercase; letter-spacing: 0.5px; }
.kpi-value { font-size: 1.25rem; font-weight: 700; color: #202124; line-height: 1.3; }
.kpi-delta { font-size: 0.75rem; font-weight: 500; margin-top: 2px; }
.kpi-subtitle { font-size: 0.75rem; color: #80868b; margin-top: 2px; }
@media (max-width: 600px) {
  .kpi-value { font-size: 1rem; }
}
</style>
